import { apiGet, apiPatch } from './client.js';
import type { ReportPeriod } from './reports.js';

export type NpsCategory = 'promoter' | 'passive' | 'detractor';

export type NpsStatsQuery = {
  period?: ReportPeriod;
  dateFrom?: string;
  dateTo?: string;
  storeId?: string;
};

export type NpsResponsesQuery = NpsStatsQuery & {
  category?: NpsCategory;
  page?: number;
  pageSize?: number;
};

export type NpsStats = {
  npsScore: number;
  promoters: number;
  passives: number;
  detractors: number;
  totalResponses: number;
  responseRate: number;
  avgScore: number;
  googleReviewsSent: number;
  followUpsRequired: number;
  trend: Array<{ date: string; npsScore: number; responses: number }>;
};

export type NpsResponse = {
  id: string;
  ticketId: string | null;
  customerId: string | null;
  storeId: string;
  status: string;
  score: number | null;
  npsCategory: NpsCategory | null;
  feedback: string | null;
  sentAt: string | null;
  respondedAt: string | null;
  googleReviewSent: boolean;
  googleReviewSentAt: string | null;
  followUpRequired: boolean;
  followUpCompleted: boolean;
  followUpNotes: string | null;
  createdAt: string;
  customerName: string | null;
  ticketNumber: string | null;
  deviceName: string | null;
};

function qString(q: NpsStatsQuery | NpsResponsesQuery): string {
  const p = new URLSearchParams();
  if (q.period) p.set('period', q.period);
  if (q.dateFrom) p.set('dateFrom', q.dateFrom);
  if (q.dateTo) p.set('dateTo', q.dateTo);
  if (q.storeId) p.set('storeId', q.storeId);
  if ('category' in q && q.category) p.set('category', q.category);
  if ('page' in q && q.page) p.set('page', String(q.page));
  if ('pageSize' in q && q.pageSize) p.set('pageSize', String(q.pageSize));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function getStats(q: NpsStatsQuery = {}): Promise<NpsStats> {
  return apiGet<NpsStats>(`/api/v1/nps/stats${qString(q)}`).then((r) => r.data);
}

export function listResponses(q: NpsResponsesQuery = {}): Promise<{
  data: NpsResponse[];
  meta: { page: number; pageSize: number; total: number };
}> {
  return apiGet<NpsResponse[], { page: number; pageSize: number; total: number }>(
    `/api/v1/nps/responses${qString(q)}`,
  ).then((r) => ({ data: r.data, meta: r.meta ?? { page: 1, pageSize: 25, total: r.data.length } }));
}

export function completeFollowUp(
  id: string,
  body: { followUpCompleted: boolean; followUpNotes?: string },
): Promise<NpsResponse> {
  return apiPatch<NpsResponse>(`/api/v1/nps/responses/${id}/follow-up`, body).then((r) => r.data);
}
